/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { getScreenshotBlob } from '../api';
import { C, FONTE_MONO } from '../paleta';

/**
 * Lightbox do print de uma ocorrência da aba Erros — overlay no padrão do
 * PopupProcessos (fundo escurecido fecha no clique; o card não propaga).
 *
 * Mesmo mecanismo do ScreenshotViewer do /monitoramento (a MESMA assimetria):
 * o endpoint exige Authorization e `<img src>` não manda header — os bytes vêm
 * por fetch autenticado (`getScreenshotBlob`) e viram objectURL, revogado na
 * troca de print e no desmonte (nunca URL crua do bucket — LGPD).
 *
 * A autorização do print é a MESMA da tela (grupo/tb_tela), como no resto do
 * sistema: quem o admin liberou para o Orquestrador vê o print — não existe
 * gate extra de admin. O 403 só sobra para quem nem à tela tem acesso, e ainda
 * ganha mensagem digna em vez de imagem quebrada.
 */

interface ModalPrintProps {
  /** Key S3 do print (print_key do item); null = modal fechado. */
  printKey: string | null;
  onClose: () => void;
}

const mensagemDoStatus = (status: number | undefined): string => {
  if (status === 403) {
    return 'Sem permissão para ver este print — seu grupo não tem acesso à tela do Orquestrador.';
  }
  if (status === 404) {
    return 'Print expirado ou não encontrado — as evidências têm prazo de retenção no bucket.';
  }
  return 'Não deu para carregar o print agora. Tente de novo em instantes.';
};

export const ModalPrint: React.FC<ModalPrintProps> = ({ printKey, onClose }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!printKey) {
      setSrc(null);
      setErro(null);
      return undefined;
    }
    let ativo = true;
    let objectUrl: string | null = null;
    setSrc(null);
    setErro(null);
    getScreenshotBlob(printKey)
      .then(blob => {
        if (!ativo) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err: { response?: { status?: number } }) => {
        if (!ativo) return;
        setErro(mensagemDoStatus(err?.response?.status));
      });
    return () => {
      ativo = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [printKey]);

  if (!printKey) return null;

  let corpo: React.ReactNode;
  if (erro) {
    corpo = (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '18px 20px',
          fontSize: '13px',
          color: 'oklch(0.4 0.1 28)',
        }}
      >
        <span>⚠</span>
        {erro}
      </div>
    );
  } else if (!src) {
    corpo = (
      <div
        style={{
          padding: '28px 20px',
          fontSize: '12.5px',
          fontStyle: 'italic',
          color: C.sub,
          textAlign: 'center',
        }}
      >
        Carregando print…
      </div>
    );
  } else {
    corpo = (
      <img
        src={src}
        alt="Print da ocorrência"
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: '76vh',
          borderRadius: '0 0 14px 14px',
        }}
      />
    );
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'oklch(0.2 0.02 165 / 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: '20px',
      }}
    >
      <div
        onClick={ev => ev.stopPropagation()}
        style={{
          background: 'oklch(1 0 0)',
          borderRadius: '14px',
          maxWidth: 'min(920px, 94vw)',
          maxHeight: '86vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px oklch(0.2 0.02 165 / 0.28)',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            borderBottom: '1px solid oklch(0.93 0.008 160)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <div style={{ fontSize: '14.5px', fontWeight: 600 }}>Print da ocorrência</div>
            <div
              title={printKey}
              style={{
                fontFamily: FONTE_MONO,
                fontSize: '10.5px',
                color: C.faint,
                maxWidth: '520px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {printKey}
            </div>
          </div>
          <div
            onClick={onClose}
            title="Fechar"
            style={{
              flex: 'none',
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'oklch(0.96 0.006 160)',
              fontSize: '13px',
              cursor: 'pointer',
              color: 'oklch(0.5 0.015 165)',
            }}
          >
            ✕
          </div>
        </div>
        {corpo}
      </div>
    </div>
  );
};
